/**
 * RoutineEditFormBody — shared form body for CREATE / ALTER {PROCEDURE|FUNCTION}.
 *
 * Engines:
 *   - mssql:    OR ALTER + body via `AS …`. Function params IN-only.
 *   - postgres: OR REPLACE + LANGUAGE + volatility / strict / security on
 *               functions. Body delimited by `$$`.
 *   - mysql:    DEFINER / DETERMINISTIC / SQL access / SQL security. Function
 *               params IN-only. No param defaults.
 *   - oracle:   OR REPLACE + EDITIONABLE / NONEDITIONABLE + AUTHID. Body via
 *               `AS …`.
 *   - sqlite:   unsupported — surface disabled (sqlite has no stored routines).
 *
 * Modes:
 *   - `create`: builds a `RoutineShellDef` and dispatches `commitCreateRoutine`.
 *   - `alter`:  builds a `RoutineChange` (oracle COMPILE / postgres RENAME)
 *               and dispatches `commitAlterRoutine`. mssql / mysql / sqlite
 *               render disabled with the documented redirect.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import {
  commitAlterRoutine,
  getAlterRoutineUnsupportedTooltip,
  isAlterRoutineSupported,
  validateAlterRoutineForEngine,
} from '../../edit/edits/alterRoutine';
import {
  commitCreateRoutine,
  getCreateRoutineUnsupportedTooltip,
  isCreateRoutineSupported,
  validateCreateRoutineForEngine,
} from '../../edit/edits/createRoutine';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { RoutineFormFields } from './RoutineFormFields';
import {
  buildRoutineShellDef,
  DEFAULT_ROUTINE_FORM_VALUES,
  makeRoutineQualifiedName,
  type RoutineEditMode,
  type RoutineFormValues,
} from './routineFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export type { RoutineEditMode, RoutineFormValues } from './routineFormHelpers';

export interface RoutineEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: RoutineEditMode;
  readonly initialValues?: Partial<RoutineFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function RoutineEditFormBody(props: RoutineEditFormBodyProps) {
  const { editContext, mode, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [values, setValues] = useState<RoutineFormValues>({
    ...DEFAULT_ROUTINE_FORM_VALUES, ...initialValues,
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (mode === 'create' && !isCreateRoutineSupported(engine)) {
    return renderUnsupported(
      testId, getCreateRoutineUnsupportedTooltip(engine), onCancel,
    );
  }
  if (mode === 'alter' && !isAlterRoutineSupported(engine)) {
    return renderUnsupported(
      testId, getAlterRoutineUnsupportedTooltip(engine), onCancel,
    );
  }

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') {
      const routine = buildRoutineShellDef(engine, values);
      const validationError = validateCreateRoutineForEngine(engine, { routine });
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitCreateRoutine(editContext, { routine });
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const routineName = makeRoutineQualifiedName(values.name);
    const change = engine === 'oracle'
      ? { recompile: true }
      : { renameTo: makeRoutineQualifiedName(values.alterRenameTo) };
    const args = { routineName, routineKind: values.kind, change };
    const validationError = validateAlterRoutineForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterRoutine(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = pending
    ? 'Saving…'
    : mode === 'create'
      ? `Create ${values.kind}`
      : engine === 'oracle' ? 'Recompile' : 'Rename';

  return (
    <div data-testid={testId} className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create routine' : 'Alter routine'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <RoutineFormFields
        engine={engine}
        mode={mode}
        values={values}
        setValues={setValues}
        testId={testId}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}

function renderUnsupported(testId: string, message: string, onCancel?: () => void) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="text-xs font-semibold">Routine</div>
      <div
        className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
        data-testid={`${testId}-unsupported`}
      >
        {message}
      </div>
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-close`}
        >
          Close
        </Button>
      </div>
    </div>
  );
}
